// MealSection.js - Fixed handleRecreate function
import React, { useRef, useState } from "react";
import ImageWithFallback from "@components/ui/ImageFallback";
import { toast } from "react-toastify";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import { ReactComponent as Time } from "@assets/images/greenTime.svg";
import { ReactComponent as Calories } from "@assets/images/redCalories.svg";
import { useDispatch, useSelector } from "react-redux";
import { addMealIndex } from "Redux/slices/selectedMealsSlice";
import { useTranslation } from "react-i18next";
import { createOneMeal } from "api/dietPlan";
import { useNavigate } from "react-router-dom";

const MealSection = ({
  mealType,
  selectedMeal,
  base_url,
  handleSetDietPlan,
  setSelectedMeals,
  selectedIndex,
  loading,
  onSetLoading,
  globalLoading,
  filteredMeal,
  countdown,
  countdownRef,
  startCountdown,
  timeoutRef,
  isMountedRef,
  startDate=null,
  weeklyMealPlanner = false,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const mealsToShow = useSelector((state) => state.selectedMeals.mealToShow);
  const navigate = useNavigate();

  // Add ref to track current recreation operation
  const recreationRequestRef = useRef(null);

  const handleSetMeal = (newMeals, mealTypeLower) => {
    const newIndex = newMeals.length - 1;
    // Only update state if we have a valid new meal
    if (newMeals[newIndex] && newMeals[newIndex].MealName) {
      setSelectedMeals((prev) => ({
        ...prev,
        [selectedMeal?.day]: {
          ...prev?.[selectedMeal?.day],
          [mealTypeLower]: newIndex,
        },
      }));

      dispatch(
        addMealIndex({
          day: selectedMeal.day,
          mealType: mealTypeLower,
          index: newIndex,
        })
      );

      toast.info(t("toast.New meal created successfully!"));
    } else {
      throw new Error("Created meal is incomplete or invalid");
    }
  };

  const handleRecreate = async () => {
    // Cancel any existing recreation request
    if (recreationRequestRef.current) {
      clearTimeout(recreationRequestRef.current);
      recreationRequestRef.current = null;
    }

    onSetLoading(true);

    try {
      const mealTypeLower = mealType.toLowerCase();
      const currentIndexes =
        mealsToShow[selectedMeal.day]?.[mealTypeLower] || [];
      const allMeals = selectedMeal.meal[mealType] || [];

      const hiddenIndex = allMeals.findIndex(
        (meal, index) => !currentIndexes.includes(index)
      );

      if (hiddenIndex >= 0) {
        // Use existing hidden meal
        startCountdown(10);
        recreationRequestRef.current = setTimeout(() => {
          // Double check component is still mounted and request is still valid
          if (!isMountedRef.current || recreationRequestRef.current === null) {
            return;
          }

          dispatch(
            addMealIndex({
              day: selectedMeal.day,
              mealType: mealTypeLower,
              index: hiddenIndex,
            })
          );

          setSelectedMeals((prev) => ({
            ...prev,
            [selectedMeal.day]: {
              ...prev[selectedMeal.day],
              [mealTypeLower]: hiddenIndex,
            },
          }));

          clearInterval(countdownRef.current);
          onSetLoading(false);
          toast.info(t("toast.New meal created successfully!"));
          recreationRequestRef.current = null;
        }, 10000);
      } else {
        // Create new meal via API
        startCountdown(60, true);

        // Create unique request ID to prevent duplicate requests
        const requestId = Date.now();
        recreationRequestRef.current = requestId;

        const response = await createOneMeal({
          mealName: selectedMeal?.meal?.[mealType][selectedIndex]?.MealName,
          day: selectedMeal.day,
          mealType,
          calories: selectedMeal?.meal?.[mealType][selectedIndex]?.Calories,
          requestId, // Send unique request ID to backend if possible
        });

        // Verify request is still valid and component is mounted
        if (
          !isMountedRef.current ||
          recreationRequestRef.current !== requestId
        ) {
          return;
        }

        // Validate response data before proceeding
        const newMeals =
          response?.data?.diet_plan?.[selectedMeal.day]?.Meals?.[mealType];

        if (!newMeals || !Array.isArray(newMeals)) {
          throw new Error("Invalid response data structure");
        }

        await handleSetDietPlan(response?.data);
        // console.log(newMeals,newIndex);

        handleSetMeal(newMeals, mealTypeLower);

        clearInterval(countdownRef.current);
        onSetLoading(false);
        recreationRequestRef.current = null;
      }
    } catch (error) {
      // Clear any pending timeouts on error
      if (
        recreationRequestRef.current &&
        typeof recreationRequestRef.current === "number"
      ) {
        clearTimeout(recreationRequestRef.current);
      }
      recreationRequestRef.current = null;

      toast.error(t("toast.Failed to create new meal. Please try again."));
      console.error("Error in handleRecreate:", error);
      onSetLoading(false);
      clearInterval(countdownRef.current);
    }
  };

  const handleOpenRecipie = () => {
    navigate("/diet-plan/recipe-detail", {
      state: {
        mealType: mealType,
        index: selectedIndex,
        day: selectedMeal.day,
        startDate
      },
    });
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (recreationRequestRef.current) {
        if (
          typeof recreationRequestRef.current === "number" &&
          recreationRequestRef.current > 1000
        ) {
          // It's a setTimeout ID
          clearTimeout(recreationRequestRef.current);
        }
        recreationRequestRef.current = null;
      }
    };
  }, []);
  // Validate meal data before rendering
  const currentMeal = filteredMeal?.meal?.[mealType]?.[selectedIndex];


  return (
    <div className="p-2 rounded-lg overflow-hidden max-h-[100%] md:flex md:justify-center">
      <div
        className="w-full xl:max-w-[76%] xl:min-w-[76%] md:p-[22px] flex flex-col gap-3 border border-[2px] border-[#F2F2F2B8] rounded-[10px] p-5 bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)]"
        style={{
          boxShadow: "0px 4px 4px 0px #0000000D",
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
          <ImageWithFallback
            src={`${
              filteredMeal?.images[mealType]?.length > 0 &&
              filteredMeal?.images[mealType][selectedIndex]
            }`}
            alt={mealType}
            width={"106px"}
            height={"106px"}
            className="min-w-[163px] min-h-[300px] w-[100%] md:h-[100%] xl:h-auto xl:max-h-[265px] rounded-lg object-cover"
          />

          <div className="space-y-2">
            <div className="flex justify-between">
              <h1 className="text-white text-[24px] font-[Lato] font-[600]">
                {t(`weeklyMealDetails.${mealType?.toLowerCase()}`)}
              </h1>
              {!weeklyMealPlanner &&
                filteredMeal?.meal?.[mealType]?.length <= 2 && (
                  <button
                    onClick={handleRecreate}
                    className={`px-3 py-2 bg-white text-[#0575e6] rounded-[100px] text-nowrap text-[10px] lg:text-[12px] xl:text-[14px] min-w-[140px] ${
                      loading || (globalLoading && "opacity-80")
                    }`}
                    disabled={loading || globalLoading}
                  >
                    {loading
                      ? `(${countdown}s)`
                      : t(`weeklyMealSelection.recreateMeal`)}
                  </button>
                )}
              {weeklyMealPlanner && (
                <button
                  onClick={handleOpenRecipie}
                  className="px-3 py-2 bg-white text-[#0575e6] rounded-[100px] text-nowrap text-[10px] lg:text-[12px] xl:text-[14px]"
                >
                  {t(`weeklyMealDetails.viewRecipe`)}
                </button>
              )}
            </div>
            <p className="text-white text-[15px] font-[Lato] font-[400]">
              {!weeklyMealPlanner &&
              filteredMeal?.meal?.[mealType]?.length > 1 ? (
                <select
                  className="text-sm text-[#0575e6] border rounded-lg p-2 max-w-[100%] bg-[#fff] w-full outline-none"
                  value={selectedIndex}
                  onChange={(e) => {
                    const newIndex = Number(e.target.value);
                    const selectedMealData =
                      filteredMeal?.meal?.[mealType]?.[newIndex];

                    // Only update if the meal data is valid
                    if (selectedMealData && selectedMealData.MealName) {
                      setSelectedMeals((prev) => ({
                        ...prev,
                        [filteredMeal?.day]: {
                          ...prev?.[filteredMeal?.day],
                          [mealType.toLowerCase()]: newIndex,
                        },
                      }));
                    }
                  }}
                >
                  {filteredMeal?.meal?.[mealType]?.map((meal, idx) => (
                    <option key={idx} value={idx} disabled={!meal?.MealName}>
                      {meal?.MealName || `Loading meal ${idx + 1}...`}
                    </option>
                  ))}
                </select>
              ) : (
                currentMeal?.MealName
              )}
            </p>
            <div className="bg-white p-4 rounded-[10px] space-y-1">
              <div className="flex flex-col xl:flex-row xl:items-center gap-2">
                <div className="text-[#4CA842] font-[600] text-[12px] bg-[#D7FFD2] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content]">
                  <p className="bg-white rounded-full min-w-7 min-h-7 flex items-center justify-center text-nowrap truncate">
                    <Time className="min-w-4 min-h-4" />
                  </p>{" "}
                  {t(`weeklyMealDetails.prepTime`)}:{" "}
                  {currentMeal?.PreparationTime?.split("(")[0]?.trim() || "N/A"}
                </div>
                <div className="text-[#fff] font-[600] text-[12px] bg-[#FFB4AD] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content]">
                  <p className="bg-white rounded-full min-w-7 min-h-7 flex items-center justify-center text-nowrap truncate">
                    <Calories className="min-w-4 min-h-4" />
                  </p>{" "}
                  {t(`weeklyMealDetails.calories`)}:
                  {currentMeal?.Calories || "N/A"}
                </div>
              </div>
              <p className="text-[14px] font-[Lato] text-[#626262]">
                {t(`weeklyMealDetails.description`)}:
              </p>
              <div className="border border-[#DADADA] p-3 rounded-[10px]">
                <p className="text-[14px] font-[Lato] text-[#6B6B6B] font-[400]">
                  {currentMeal?.Summary || "Loading description..."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MealSection;
